import './loadEnv.js'

import { createLogger } from '../../common/utils/logger.js'

export type RedisDriver = 'upstash' | 'ioredis'

const logger = createLogger('redis-config')

const normalizeUpstashUrl = (raw?: string | null): string | undefined => {
  if (!raw) return undefined
  const trimmed = raw.trim()
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed
  }
  return `https://${trimmed}`
}

const resolveUpstashHost = (url?: string): string | undefined => {
  if (!url) return undefined
  try {
    return new URL(url).hostname
  } catch (error) {
    logger.warn('Invalid UPSTASH_REDIS_REST_URL, expected full URL or host', { url, error })
    return undefined
  }
}

const rawUpstashUrl = process.env.UPSTASH_REDIS_REST_URL
const upstashUrl = normalizeUpstashUrl(rawUpstashUrl)
const upstashToken = process.env.UPSTASH_REDIS_REST_TOKEN
const upstashPort = Number.parseInt(process.env.UPSTASH_REDIS_PORT || process.env.REDIS_PORT || '6379', 10)
const rawDriver = process.env.REDIS_DRIVER?.toLowerCase()
const hasUpstashCreds = Boolean(upstashUrl && upstashToken)

const redisHost = process.env.REDIS_HOST || 'localhost'
const redisPort = Number.parseInt(process.env.REDIS_PORT || '6379', 10)
const redisPassword = process.env.REDIS_PASSWORD
const redisTls = process.env.REDIS_TLS === 'true'

const redisDriver: RedisDriver = (() => {
  if (rawDriver === 'local' || rawDriver === 'ioredis') {
    logger.info('Forcing local Redis (ioredis) via REDIS_DRIVER', { redisDriverOverride: rawDriver })
    return 'ioredis'
  }

  if (rawDriver === 'upstash') {
    if (!hasUpstashCreds) {
      logger.warn('REDIS_DRIVER=upstash but UPSTASH_REDIS_REST_URL/TOKEN are missing, falling back to local Redis')
      return 'ioredis'
    }
    logger.info('Forcing Upstash Redis (REDIS_DRIVER=upstash)')
    return 'upstash'
  }

  if (rawDriver && rawDriver !== 'auto') {
    logger.warn('Unknown REDIS_DRIVER value (use auto | upstash | local | ioredis). Falling back to auto-detect.', { redisDriverOverride: rawDriver })
  }

  if (hasUpstashCreds) {
    logger.info('UPSTASH_REDIS_REST_URL/TOKEN detected -> auto-selecting Upstash Redis', { upstashUrl })
    return 'upstash'
  }

  logger.info('Using local Redis (auto-detect)')
  return 'ioredis'
})()

const driverSource: 'forced' | 'auto' =
  rawDriver && ['local', 'ioredis', 'upstash'].includes(rawDriver) ? 'forced' : 'auto'

const upstashHost = resolveUpstashHost(upstashUrl)

export const redisConfig = {
  driver: redisDriver,
  driverSource,
  upstashUrl,
  upstashHost,
  upstashToken,
  upstashPort,
  redisHost,
  redisPort,
  redisPassword,
  redisTls,
  hasUpstashCreds
}

export const supportsRateLimitRedis = redisDriver === 'ioredis'

export const bullRedisConfig = redisDriver === 'upstash' && hasUpstashCreds && upstashHost
  ? {
    host: upstashHost ?? 'upstash',
    port: upstashPort,
    password: upstashToken,
    tls: {}
  }
  : {
    host: redisHost,
    port: redisPort,
    password: redisPassword || undefined,
    tls: redisTls ? {} : undefined
  }

export { redisDriver }
